import { Component, Input, computed, inject, signal } from '@angular/core';

import { SimulatedTimeService } from '../global/simulated-time/simulated-time.service';
import { EventScheduleBlock } from './models';

/**
 * An event's schedule, read-only, laid out proportionally to each block's duration
 * with a Google-Calendar-style line marking the current time.
 *
 * Extracted from the event day page so the same presentation can be used anywhere a
 * schedule is shown. The organiser-facing editor is a different thing entirely -- see
 * the admin schedule-timeline component, which edits blocks rather than presenting them.
 */
@Component({
    standalone: false,
    selector: 'abs-event-schedule',
    template: `
        <ion-list class="event-schedule">
            @if (heading) {
                <ion-list-header>{{ heading }}</ion-list-header>
            }
            <div class="event-schedule-track">
                @for (row of rows(); track row.startTime) {
                    <!-- ngClass, not [class]: a bare [class] binding replaces the static class,
                         which drops .event-schedule-row and with it the --min-height reset that
                         lets the height below actually take effect. -->
                    <ion-item
                        class="event-schedule-row"
                        [ngClass]="'event-schedule-row--' + row.type"
                        [class.event-schedule-row--expandable]="!!row.description"
                        [class.event-schedule-row--expanded]="expandedIndex() === $index"
                        [style.height.px]="row.height"
                        [button]="!!row.description"
                        [detail]="false"
                        (click)="toggle($index, row.description)"
                    >
                        <!-- Time first, hard against the label, because a label on the far left
                             and its time on the far right is the thing that makes this list hard
                             to read: the eye has to travel the full row width to pair them. -->
                        <span class="event-schedule-time" slot="start">{{ row.startTime | date: 'shortTime' }}</span>
                        <ion-icon class="event-schedule-icon" slot="start" [name]="row.icon"></ion-icon>
                        <ion-label class="event-schedule-label">
                            <span class="event-schedule-block-name">{{ row.label }}</span>
                            <!-- Read on the row itself rather than behind a tap. The row cannot
                                 grow to fit it (see descriptionLineCount), so the text is clamped
                                 to the lines this row's own height allows and the tap-through
                                 below is what shows the rest of a long one. -->
                            @if (row.description) {
                                <span
                                    class="event-schedule-description"
                                    [style.-webkit-line-clamp]="row.descriptionLines"
                                >{{ row.description }}</span>
                            }
                        </ion-label>
                        @if (row.description) {
                            <ion-icon
                                class="event-schedule-expand"
                                slot="end"
                                [name]="expandedIndex() === $index ? 'chevron-up-outline' : 'chevron-down-outline'"
                            ></ion-icon>
                        }
                    </ion-item>
                }
                @if (nowOffset() !== null) {
                    <div class="event-schedule-now" [style.top.px]="nowOffset()">
                        <span class="event-schedule-now-time">{{ time.now() | date: 'shortTime' }}</span>
                    </div>
                }
            </div>
            <!-- Outside the track on purpose. Row heights are proportional to duration and the
                 now-line is positioned from those same numbers, so growing a row to fit text
                 would both squash the scale and move the line off the time it marks. -->
            @if (expanded(); as row) {
                <div class="event-schedule-detail">
                    <h4>{{ row.label }}</h4>
                    <p>{{ row.description }}</p>
                </div>
            }
        </ion-list>
    `
})
export class EventScheduleComponent {
    readonly time = inject(SimulatedTimeService);

    /** Optional list heading. Omitted when the host already provides one. */
    @Input() heading?: string;

    @Input()
    set blocks(value: EventScheduleBlock[] | undefined | null) {
        this.source.set([...(value ?? [])].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()));
        // A new schedule invalidates the open row: the index would otherwise point at whatever
        // block has taken that position.
        this.expandedIndex.set(null);
    }

    private readonly source = signal<EventScheduleBlock[]>([]);

    /** Which row's description is open, by index into rows(). Null when none is. */
    readonly expandedIndex = signal<number | null>(null);

    readonly expanded = computed(() => {
        const index = this.expandedIndex();
        return index === null ? undefined : this.rows()[index];
    });

    /**
     * Opens a row's description, or closes it if it is already open. A row with nothing to say
     * is inert - tapping it must not silently close the one the reader has open.
     */
    toggle(index: number, description: string | undefined) {
        if (!description) {
            return;
        }
        this.expandedIndex.update((current) => (current === index ? null : index));
    }

    /**
     * Blocks with a pixel height derived from their duration, plus the running offset
     * of each from the top of the track.
     *
     * Heights are computed here rather than measured from the DOM so the now-line can
     * be positioned from the same numbers -- measuring after render would let the two
     * disagree for a frame, and the line would visibly jump.
     */
    readonly rows = computed(() => {
        const blocks = this.source();
        let offset = 0;

        return blocks.map((block, index) => {
            const startMs = new Date(block.startTime).getTime();
            // A block runs until the next one starts. The last has no successor, so it
            // gets a nominal length purely so it occupies a sensible amount of space.
            const endMs = index < blocks.length - 1 ? new Date(blocks[index + 1].startTime).getTime() : startMs + LAST_BLOCK_MINUTES * 60_000;
            const durationMs = Math.max(endMs - startMs, 60_000);
            // Clamped, so a 20-minute break stays readable next to a 2-hour round. This
            // makes the layout approximately rather than strictly to scale: two short
            // blocks of different lengths can end up the same height. Worth it -- the
            // honest version renders short blocks too small to read.
            const height = Math.max(MIN_ROW_PX, (durationMs / 60_000) * PX_PER_MINUTE);
            const row = {
                ...block,
                icon: BLOCK_ICONS[block.type] ?? DEFAULT_ICON,
                startMs,
                durationMs,
                height,
                offsetTop: offset,
                descriptionLines: descriptionLineCount(height)
            };
            offset += height;
            return row;
        });
    });

    /**
     * Where the now-line sits, in px from the top of the track, or null when the
     * current time falls outside the schedule -- pinning it to one end on every other
     * day of the year would be noise, not information.
     */
    readonly nowOffset = computed<number | null>(() => {
        const rows = this.rows();
        if (!rows.length) return null;

        const now = this.time.nowMs();
        const last = rows[rows.length - 1];
        if (now < rows[0].startMs || now > last.startMs + last.durationMs) {
            return null;
        }

        const current = rows.find((row) => now >= row.startMs && now < row.startMs + row.durationMs) ?? last;
        const fraction = (now - current.startMs) / current.durationMs;
        return current.offsetTop + fraction * current.height;
    });
}

/** Vertical scale. Tuned so a 2-hour round is tall without a whole day needing a scroll. */
const PX_PER_MINUTE = 0.8;

/** Floor for a row, so its time and label always fit however short the block is. */
const MIN_ROW_PX = 52;

/** Room the block's own label and the row's padding need, before any description. */
const LABEL_RESERVED_PX = 26;

/** One line of description, at the size events.scss renders it. */
const DESCRIPTION_LINE_PX = 17;

/**
 * At most this many lines, however tall the row. A lunch block spanning an hour has the room
 * for a dozen lines, but a schedule is meant to be scanned - past this the note stops being a
 * glance and starts being a document, and the tap-through is the place for it.
 */
const MAX_DESCRIPTION_LINES = 8;

/**
 * How many lines of description a row of this height can show.
 *
 * The rows are scaled to duration and the now-line is positioned from those same heights, so a
 * row cannot grow to fit its text - the text is clamped to the row instead. Always at least one
 * line: a block showing none would read as a block with nothing to say.
 */
export function descriptionLineCount(rowHeightPx: number): number {
    const available = Math.floor((rowHeightPx - LABEL_RESERVED_PX) / DESCRIPTION_LINE_PX);
    return Math.min(Math.max(available, 1), MAX_DESCRIPTION_LINES);
}

/** Nominal length for the final block, which has no successor to end it. */
const LAST_BLOCK_MINUTES = 30;

const DEFAULT_ICON = 'ellipse-outline';

/** One icon per block type, so a round is distinguishable from a break at a glance. */
const BLOCK_ICONS: Record<string, string> = {
    round: 'shield-outline',
    break: 'cafe-outline',
    registration: 'clipboard-outline',
    results: 'trophy-outline',
    day_end: 'flag-outline'
};
